import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { formatXof } from '../utils/format';

const AID_TYPES = ['Remise', 'Bourse', 'Exonération', 'Réduction exceptionnelle'];
const AID_STATUSES = ['Accordée', 'En attente', 'Suspendue', 'Annulée'];
const STATUS_COLOR = { 'Accordée': 'green', 'En attente': 'gold', 'Suspendue': 'orange', 'Annulée': 'red' };

const EMPTY = {
  studentId: '', studentName: '', className: '', aidType: 'Bourse', description: '',
  amountXof: '', percentage: '', feeType: '', startDate: '', endDate: '',
  status: 'Accordée', approvedBy: '', donorName: '', donorContact: '', conditions: '', notes: '',
};

export default function Bourses() {
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');

  function load() {
    api.get('/registry/scholarships').then((d) =>
      setRows((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })))
    ).catch(() => {});
  }

  useEffect(() => {
    load();
    api.get('/students').then(setStudents).catch(() => {});
  }, []);

  const filtered = rows.filter((r) => !typeFilter || r.aidType === typeFilter);
  const totalAides = rows.reduce((s, r) => s + Number(r.amountXof || 0), 0);

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi"><span>Aides accordées</span><strong>{rows.filter((r) => r.status === 'Accordée').length}</strong></div>
        <div className="kpi gold"><span>Total aides</span><strong>{formatXof(totalAides)}</strong></div>
        <div className="kpi navy"><span>Bourses</span><strong>{rows.filter((r) => r.aidType === 'Bourse').length}</strong></div>
        <div className="kpi"><span>Exonérations</span><strong>{rows.filter((r) => r.aidType === 'Exonération').length}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Bourses & exonérations</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 180 }}>
              <option value="">Tous les types</option>
              {AID_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <button className="btn btn-sm" onClick={() => setModal({ ...EMPTY })}>+ Ajouter</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {filtered.length === 0 ? <div className="empty">Aucune aide financière.</div> : (
            <div className="table-wrap"><table>
              <thead>
                <tr><th>Élève</th><th>Type</th><th>Frais concerné</th><th className="num">Montant</th><th className="num">%</th><th>Période</th><th>Approuvé par</th><th>Statut</th><th></th></tr>
              </thead>
              <tbody>{filtered.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.studentName || '—'}</b></td>
                  <td>{r.className || '—'}</td>
                  <td><span className="badge navy">{r.aidType}</span></td>
                  <td>{r.feeType || '—'}</td>
                  <td className="num">{r.amountXof ? formatXof(r.amountXof) : '—'}</td>
                  <td className="num">{r.percentage ? `${r.percentage}%` : '—'}</td>
                  <td className="mono" style={{ fontSize: '0.8rem' }}>
                    {r.startDate ? new Date(r.startDate).toLocaleDateString('fr-FR') : '—'}
                    {r.endDate ? ` → ${new Date(r.endDate).toLocaleDateString('fr-FR')}` : ''}
                  </td>
                  <td>{r.donorName || '—'}</td>
                  <td><span className={`badge ${STATUS_COLOR[r.status] || 'gray'}`}>{r.status}</span></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn-ghost btn-sm" onClick={() => setView(r)}>Fiche</button>
                    <button className="btn-ghost btn-sm" onClick={() => setModal(r)}>Modifier</button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {modal && <BourseModal initial={modal} students={students} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {view && <ViewModal item={view} onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} fields={[['Élève', 'studentName'], ['Type d aide', 'aidType'], ['Frais concerné', 'feeType'], ['Montant', 'amountXof'], ['Pourcentage', 'percentage'], ['Date début', 'startDate'], ['Date fin', 'endDate'], ['Approuvé par', 'approvedBy'], ['Statut', 'status'], ['Conditions', 'conditions'], ['Notes', 'notes']]} title="Bourse" />}
    </>
  );
}

function BourseModal({ initial, students, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  function onStudentChange(e) {
    const id = e.target.value;
    const s = students.find((s) => s.id === id);
    setF((p) => ({
      ...p,
      studentId: id,
      studentName: s ? `${s.lastName} ${s.firstName}` : p.studentName,
      className: s?.className || s?.classId || p.className,
    }));
  }

  async function save() {
    if (!f.aidType || (!f.amountXof && !f.percentage)) { setError('Type et montant ou pourcentage sont obligatoires.'); return; }
    setSaving(true);
    try {
      const payload = { data: f, label: `${f.aidType} - ${f.studentName || ''}`, searchText: `${f.studentName || ''} ${f.aidType}`, refDate: f.startDate || undefined };
      if (isEdit) await api.patch(`/registry/scholarships/${initial.id}`, payload);
      else await api.post('/registry/scholarships', payload);
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Modifier l\'aide' : 'Nouvelle aide financière'}</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="fieldset-title">Bénéficiaire & type</div>
        <div className="grid-3">
          <div className="field"><label>Élève</label>
            <select value={f.studentId} onChange={onStudentChange}>
              <option value="">Sélectionner…</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
            </select>
          </div>
          <div className="field"><label>Type d'aide *</label>
            <select value={f.aidType} onChange={set('aidType')}>
              {AID_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Frais concerné</label>
            <input value={f.feeType} onChange={set('feeType')} placeholder="Ex : Frais de scolarité" />
          </div>
        </div>
        <div className="fieldset-title">Montant & période</div>
        <div className="grid-3">
          <div className="field"><label>Montant (XOF)</label><input type="number" min="0" value={f.amountXof} onChange={set('amountXof')} /></div>
          <div className="field"><label>Pourcentage (%)</label><input type="number" min="0" max="100" value={f.percentage} onChange={set('percentage')} /></div>
          <div className="field"><label>Statut</label>
            <select value={f.status} onChange={set('status')}>
              {AID_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="field"><label>Date début</label><input type="date" value={f.startDate} onChange={set('startDate')} /></div>
          <div className="field"><label>Date fin</label><input type="date" value={f.endDate} onChange={set('endDate')} /></div>
          <div className="field"><label>Approuvé par</label><input value={f.approvedBy} onChange={set('approvedBy')} /></div>
          <div className="field"><label>Classe (auto)</label><input value={f.className || ''} onChange={set('className')} placeholder="Auto depuis fiche élève" /></div>
        </div>
        <div className="fieldset-title">Donneur / Partenaire</div>
        <div className="grid-3">
          <div className="field"><label>Nom du donneur</label><input value={f.donorName || ''} onChange={set('donorName')} placeholder="Ex : État, ONG, Mécène..." /></div>
          <div className="field"><label>Contact donneur</label><input value={f.donorContact || ''} onChange={set('donorContact')} /></div>
        </div>
        <div className="field"><label>Description</label><textarea value={f.description} onChange={set('description')} rows={2} /></div>
        <div className="field"><label>Conditions</label><textarea value={f.conditions} onChange={set('conditions')} rows={2} /></div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

function ViewModal({ item, title, fields, onClose, onEdit }) {
  if (!item) return null;
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>Fiche — {title}</h2>
        <div className="view-grid">
          {fields.map(([label, key]) => item[key] != null && item[key] !== '' ? (
            <div key={key} className="view-row">
              <span className="view-label">{label}</span>
              <span className="view-value">{String(item[key])}</span>
            </div>
          ) : null)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
